/**
 * Offline-first cache + mutation queue for DefectR.
 *
 * Two responsibilities:
 *   1. SNAPSHOT — keep a copy of the user's projects in IndexedDB so the
 *      app loads instantly even with no network.
 *   2. QUEUE — when a mutation (create/update/delete) fails because the
 *      device is offline, push it to a queue that drains automatically the
 *      moment connectivity returns.
 *
 * The queue is intentionally small in scope — only the mutations that
 * matter when walking around a new build with patchy signal:
 *   addSnag · updateSnag · deleteSnag · addRoom · deleteRoom
 *
 * Heavier flows (floorplan uploads, AI analysis, share links, billing)
 * stay online-only — those are rare and benefit from immediate feedback.
 */

import { openDB } from 'idb';

const DB_NAME = 'defectr-offline';
const DB_VERSION = 1;
const SNAPSHOT_STORE = 'snapshot';
const QUEUE_STORE = 'mutation-queue';

let dbPromise = null;

function getDB() {
    if (!dbPromise) {
        dbPromise = openDB(DB_NAME, DB_VERSION, {
            upgrade(db) {
                if (!db.objectStoreNames.contains(SNAPSHOT_STORE)) {
                    db.createObjectStore(SNAPSHOT_STORE);
                }
                if (!db.objectStoreNames.contains(QUEUE_STORE)) {
                    db.createObjectStore(QUEUE_STORE, { keyPath: 'id', autoIncrement: true });
                }
            },
        });
    }
    return dbPromise;
}

// ---------- Snapshot -------------------------------------------------------

export async function loadCachedProjects(userId) {
    if (!userId) return null;
    try {
        const db = await getDB();
        const data = await db.get(SNAPSHOT_STORE, `projects:${userId}`);
        return Array.isArray(data) ? data : null;
    } catch {
        return null;
    }
}

export async function saveCachedProjects(userId, projects) {
    if (!userId) return;
    try {
        const db = await getDB();
        await db.put(SNAPSHOT_STORE, projects, `projects:${userId}`);
    } catch { /* IDB unavailable in private mode etc — never block the UI */ }
}

// ---------- Mutation queue -------------------------------------------------

/**
 * Enqueue a mutation for later replay.
 *  type:      one of 'addSnag', 'updateSnag', 'deleteSnag',
 *             'addRoom', 'deleteRoom'
 *  payload:   plain-JSON args required to replay the mutation
 *  tempId:    client-generated id for any optimistic record (so the UI
 *             can render it in lists immediately)
 */
export async function enqueueMutation(type, payload, tempId = null) {
    try {
        const db = await getDB();
        const id = await db.add(QUEUE_STORE, {
            type,
            payload,
            tempId,
            ts: Date.now(),
        });
        return id;
    } catch {
        return null;
    }
}

export async function getQueue() {
    try {
        const db = await getDB();
        return await db.getAll(QUEUE_STORE);
    } catch {
        return [];
    }
}

export async function getQueueLength() {
    try {
        const db = await getDB();
        return await db.count(QUEUE_STORE);
    } catch {
        return 0;
    }
}

export async function removeFromQueue(id) {
    try {
        const db = await getDB();
        await db.delete(QUEUE_STORE, id);
    } catch { /* noop */ }
}

export async function clearQueue() {
    try {
        const db = await getDB();
        await db.clear(QUEUE_STORE);
    } catch { /* noop */ }
}

/** True iff the most recent error from a network request looked like a
 *  connectivity failure rather than a real HTTP error. We DO want to queue
 *  on TimeoutError, NetworkError, "Failed to fetch", or `navigator.onLine`
 *  being false; we DON'T want to queue on a 4xx/5xx — those are real
 *  application errors that the user needs to see immediately. */
export function isOfflineError(err) {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
    if (!err) return false;
    // Axios sets err.response only when the server returned a status code.
    // No response + a request object usually means network-layer failure.
    if (err.response) return false;
    if (err.request) return true;
    const msg = String(err.message || '').toLowerCase();
    return msg.includes('network')
        || msg.includes('fetch')
        || msg.includes('timeout')
        || msg.includes('offline');
}
